import { cn } from '@/lib/utils'
import LinkWithTransition from '@/v2/components/helpers/link-with-transition'
import ResponsiveImage from '@/v2/components/helpers/responsive-image'
import SectionHead from '@/v2/components/sections/landing-pages/section-head'
import { Button } from '@/v2/components/ui/button'
import SectionWrapper from '@/v2/components/wrappers/section-wrapper'
import { staggerContainer } from '@/v2/constants/motion'
import { ImageType, LinkType } from '@/v2/types'
import { AnimatePresence, domAnimation, LazyMotion, m } from 'framer-motion'
import React from 'react'

type ContentItemType =
  | {
      title?: string
      description?: string
      points?: string[]
    }
  | string

type HelperArticleType = {
  content: ContentItemType[]
  image: ImageType
  link?: LinkType
}

interface HelperSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string
  description?: string
  articles: HelperArticleType[]
  ctaButton?: LinkType
  isInverted?: boolean
  buttonClassName?: string
  isBgDark?: boolean
  imgClassName?: string
  sectionTitleClassName?: string
}

const HelperSection: React.FC<HelperSectionProps> = ({
  title,
  description,
  articles,
  ctaButton,
  className,
  isInverted = false,
  isBgDark = false,
  buttonClassName,
  imgClassName,
  sectionTitleClassName
}) => {
  const imageVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 120, damping: 15 } }
  }

  const contentVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0, transition: { type: 'spring', stiffness: 100, damping: 25, delay: 0.2 } }
  }

  return (
    <LazyMotion features={domAnimation}>
      <m.section
        initial='visible'
        whileInView='visible'
        viewport={{ once: false, amount: 0.25 }}
        className={cn('landing-pages-wr section-spacing', isBgDark ? 'bg-[#F9F8F8]' : '', className)}
      >
        <SectionWrapper className='xl:max-w-[1100px]'>
          <SectionHead title={title} description={description} className={sectionTitleClassName} />

          <AnimatePresence>
            {articles.map((article, index) => (
              <div key={index}>
                <m.div
                  key={index}
                  variants={staggerContainer(0.5, 0)}
                  initial='hidden'
                  whileInView='visible'
                  viewport={{ once: false, amount: 0.25 }}
                  className={cn(
                    'flex items-center lg-mn:gap-10 gap-5 md-mn:flex-row flex-col justify-center',
                    (isInverted ? index % 2 == 0 : index % 2 == 1)
                      ? 'md-mn:!flex-row-reverse odd-article'
                      : 'even-article'
                  )}
                >
                  <m.div variants={imageVariants} className='w-full'>
                    <ResponsiveImage
                      src={article.image.src}
                      alt={article.image.alt}
                      className={cn(
                        'w-[257px] h-[144px] md-mn:w-[296px] md-mn:h-[166px] xl-min:!h-[387px] xl-min:!w-[618px] object-contain',
                        imgClassName
                      )}
                      imgClassName={imgClassName}
                    />
                  </m.div>

                  <m.div variants={contentVariants} className='w-full'>
                    {article.content?.map((item, idx) => {
                      if (typeof item === 'string') {
                        return (
                          <m.ul
                            key={idx}
                            className='xl:mb-6 xl-min:!w-[465px] mb-3 ml-7 lg-mn:ml-0 list-inside list-disc text-paragraph text-base font-normal leading-6 break-words'
                          >
                            <li
                              dangerouslySetInnerHTML={{ __html: item }}
                              className='text-paragraph font-normal leading-6 text-base break-words list-outside'
                            />
                          </m.ul>
                        )
                      } else {
                        return (
                          <m.div
                            key={item.title}
                            className='content-container xl-min:!mb-6 xl-min:!w-[465px] mb-3 [&>h3]:font-semibold xl-min:[&>h3]:text-xl [&>h3]:text-lg lg-mn:[&>h3]:mb-[10px] [&>h3]:text-paragraph [&>h3]:leading-8'
                          >
                            {item.title && <h3>{item.title}</h3>}
                            {item.description && (
                              <p className='text-paragraph font-normal leading-6 text-base mb-3'>{item.description}</p>
                            )}
                            {item.points && (
                              <ul className='list-outside list-disc ml-7'>
                                {item.points.map((point, pointIdx) => (
                                  <li
                                    key={pointIdx}
                                    dangerouslySetInnerHTML={{ __html: point }}
                                    className='text-paragraph font-normal leading-6 text-base break-words'
                                  />
                                ))}
                              </ul>
                            )}
                          </m.div>
                        )
                      }
                    })}

                    {article.link && (
                      <LinkWithTransition href={article.link.href} className='mt-[32px] w-full xl-min:block hidden'>
                        <Button
                          variant='call_to_action'
                          className={cn(
                            'transition-all animate-bounce hover:animated-paused lg-mn:w-[208px] xl-min:h-[50px] w-[191px] h-[44px]',
                            buttonClassName
                          )}
                        >
                          {article.link.text}
                        </Button>
                      </LinkWithTransition>
                    )}
                  </m.div>
                </m.div>

                {article.link && (
                  <LinkWithTransition
                    href={article.link.href}
                    className='mt-[32px] w-full xl-min:hidden flex justify-center'
                  >
                    <Button
                      variant='call_to_action'
                      className={cn(
                        'transition-all animate-bounce hover:animated-paused lg-mn:w-[208px] xl-min:h-[50px] w-[191px] h-[44px]',
                        buttonClassName
                      )}
                    >
                      {article.link.text}
                    </Button>
                  </LinkWithTransition>
                )}
              </div>
            ))}
            {ctaButton && (
              <LinkWithTransition href={ctaButton.href} className='mt-[32px] w-full flex justify-center'>
                <Button
                  variant='call_to_action'
                  className={cn(
                    'transition-all animate-bounce hover:animated-paused lg-mn:w-[208px] xl-min:h-[50px] w-[191px] h-[44px]',
                    buttonClassName
                  )}
                >
                  {ctaButton.text}
                </Button>
              </LinkWithTransition>
            )}
          </AnimatePresence>
        </SectionWrapper>
      </m.section>
    </LazyMotion>
  )
}

export default HelperSection
